"use client";

import React, { useEffect, useRef, useState } from "react";

const steps = [
  {
    number: "01",
    title: "Connect your accounts",
    description: "Link Gmail, Outlook, or both in under 60 seconds. ElevateEmail reads your history to build your communication profile — tone, habits, priorities.",
    detail: "Supports multi-account setups. Your data is encrypted end-to-end and never used for training.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    stat: "< 60 sec setup",
    statColor: "#22C55E",
  },
  {
    number: "02",
    title: "AI learns your world",
    description: "Over 7 days, ElevateEmail maps your relationships, preferred meeting windows, writing style, and triage priorities. No manual configuration.",
    detail: "The longer you use it, the smarter it gets — Ghostwriter accuracy improves 40% by week 4.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M12 2a10 10 0 1 0 10 10" stroke="#818CF8" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M12 6v6l4 2" stroke="#818CF8" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M22 2 12 12" stroke="#818CF8" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    stat: "7-day onboarding",
    statColor: "#818CF8",
  },
  {
    number: "03",
    title: "Reclaim your day",
    description: "Wake up to your Elevate Brief. Approve AI-drafted replies in one tap. Let Airlock handle every scheduling request. Your calendar, inbox, and focus — protected.",
    detail: "Average user reclaims 3.2 hours daily by week 2. Zero-click on 78% of scheduling requests.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22C55E" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M22 4 12 14.01l-3-3" stroke="#22C55E" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    stat: "3.2 hrs saved/day",
    statColor: "#F59E0B",
  },
];

const HowItWorksSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-cycle through steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="relative py-32 px-6 overflow-hidden"
      style={{ background: "#0D0F14" }}
    >
      {/* Section divider */}
      <div className="max-w-6xl mx-auto mb-20">
        <div className="section-divider" />
      </div>

      {/* Background orb */}
      <div
        className="absolute left-0 top-1/2 pointer-events-none"
        style={{
          width: "400px", height: "400px",
          background: "radial-gradient(ellipse at center, rgba(129,140,248,0.07) 0%, transparent 70%)",
          transform: "translate(-30%, -50%)",
          filter: "blur(60px)",
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div
          className="text-center mb-20"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-xs font-semibold tracking-widest uppercase"
            style={{ background: "rgba(129,140,248,0.08)", border: "1px solid rgba(129,140,248,0.15)", color: "#818CF8" }}
          >
            Simple Onboarding
          </div>
          <h2
            className="font-display leading-tight mb-4"
            style={{ fontSize: "clamp(36px, 5vw, 60px)", color: "#F0EDE8", letterSpacing: "-0.02em" }}
          >
            Up and running in{" "}
            <span className="font-display italic" style={{ color: "#F59E0B" }}>3 steps.</span>
          </h2>
          <p className="max-w-lg mx-auto" style={{ color: "#9B9891", fontSize: "18px", lineHeight: "1.6" }}>
            No IT department required. No workflow overhaul. Just connect, learn, and reclaim.
          </p>
        </div>

        {/* Steps layout — asymmetric 60/40 */}
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* Left: Step list (3/5) */}
          <div className="lg:col-span-3 space-y-4">
            {steps.map((step, idx) => (
              <div
                key={step.number}
                className="relative rounded-2xl p-6 cursor-pointer transition-all duration-400"
                style={{
                  background: activeStep === idx ? "linear-gradient(145deg, #1A1D26, #13151C)" : "transparent",
                  border: `1px solid ${activeStep === idx ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.04)"}`,
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateX(0)" : "translateX(-24px)",
                  transition: `opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 0.12}s, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 0.12}s, background 0.3s ease, border-color 0.3s ease`,
                }}
                onClick={() => setActiveStep(idx)}
              >
                <div className="flex items-start gap-5">
                  {/* Step number + connector */}
                  <div className="flex flex-col items-center">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 font-display font-semibold text-lg transition-all duration-300"
                      style={{
                        background: activeStep === idx ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${activeStep === idx ? "rgba(245,158,11,0.3)" : "rgba(255,255,255,0.06)"}`,
                        color: activeStep === idx ? "#F59E0B" : "#5C5A56",
                      }}
                    >
                      {step.number}
                    </div>
                    {idx < steps.length - 1 && (
                      <div
                        className="w-px mt-2 flex-1"
                        style={{
                          height: "40px",
                          background: `linear-gradient(to bottom, ${activeStep === idx ? "rgba(245,158,11,0.3)" : "rgba(255,255,255,0.06)"}, transparent)`,
                        }}
                      />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3
                        className="font-semibold"
                        style={{ fontSize: "18px", color: activeStep === idx ? "#F0EDE8" : "#9B9891", letterSpacing: "-0.01em", transition: "color 0.3s" }}
                      >
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: "#5C5A56" }}>
                      {step.description}
                    </p>
                    {activeStep === idx && (
                      <div
                        className="mt-4 flex items-center gap-2"
                        style={{ opacity: 1, transition: "opacity 0.3s" }}
                      >
                        <span
                          className="text-xs font-semibold px-3 py-1 rounded-full"
                          style={{ background: `${step.statColor}15`, color: step.statColor, border: `1px solid ${step.statColor}30` }}
                        >
                          {step.stat}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Active visual (2/5) */}
          <div
            className="lg:col-span-2 sticky top-28"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(24px)",
              transition: "all 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.3s",
            }}
          >
            <div
              className="rounded-3xl p-8 space-y-6"
              style={{
                background: "linear-gradient(145deg, #1A1D26 0%, #13151C 100%)",
                border: "1px solid rgba(255,255,255,0.07)",
                boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
              }}
            >
              {/* Icon */}
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
              >
                {steps[activeStep].icon}
              </div>

              {/* Step info */}
              <div>
                <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#5C5A56" }}>
                  Step {steps[activeStep].number}
                </div>
                <h4 className="font-semibold text-xl mb-3" style={{ color: "#F0EDE8", letterSpacing: "-0.01em" }}>
                  {steps[activeStep].title}
                </h4>
                <p className="text-sm leading-relaxed" style={{ color: "#9B9891" }}>
                  {steps[activeStep].detail}
                </p>
              </div>

              {/* Progress dots */}
              <div className="flex gap-2 pt-2">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveStep(i)}
                    className="transition-all duration-300 rounded-full"
                    style={{
                      width: i === activeStep ? "24px" : "8px",
                      height: "8px",
                      background: i === activeStep ? "#F59E0B" : "rgba(255,255,255,0.12)",
                    }}
                    aria-label={`Step ${i + 1}`}
                  />
                ))}
              </div>

              {/* Stats */}
              <div
                className="grid grid-cols-2 gap-4 pt-4"
                style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
              >
                {[
                  { value: "78%", label: "auto-scheduled" },
                  { value: "3.2h", label: "daily reclaimed" },
                  { value: "< 1s", label: "AI response" },
                  { value: "99.9%", label: "uptime SLA" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="font-display font-semibold text-xl" style={{ color: "#F59E0B", letterSpacing: "-0.02em" }}>{s.value}</div>
                    <div className="text-xs" style={{ color: "#5C5A56" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;